/**
 * Area → district, shared by the map and the sale counter's rent-agreement
 * rule. The book records localities ("Chinhat", "Kursi Road"); a word inside
 * the area that names a district wins, and everything else is a Lucknow
 * locality — the network is Lucknow-centred. "Sitapur Road" is the Lucknow
 * road, not the district, so road names are checked first.
 */

const ROAD_IN_LUCKNOW = /\b(SITAPUR|HARDOI|KANPUR|RAEBARELI|FAIZABAD|MOHAN|JEHTA|KURSI|IIM)\s+ROAD\b/;

const DISTRICT_WORDS: Array<[RegExp, string]> = [
  [/\bBARABANKI\b|\bTIKAITNAGAR\b|\bTIKETGANJ\b|\bDEVA\b|\bHAIDERGARH\b/, 'Barabanki'],
  [/\bKANPUR\b/, 'Kanpur Nagar'],
  [/\bUNNAO\b|\bSOHRAMAU\b|\bSHUKLAGANJ\b/, 'Unnao'],
  // Khairabad, Hargaon, Reusa and Kamlapur are FAB EV's Sitapur-district points (7 Sep 2026).
  [/\bSITAPUR\b|\bSIDHAULI\b|\bMAHOLI\b|\bMISHRIK\b|\bLAHARPUR\b|\bBISWAN?\b|\bMAHMUDABAD\b|\bKHAIRABAD\b|\bHARGAON\b|\bREUSA\b|\bKAMLAPUR\b/, 'Sitapur'],
  [/\bHARDOI\b|\bSANDILA\b|\bSHAHABAD\b|\bBILGRAM\b/, 'Hardoi'],
  [/\bRAEBARELI\b|\bRAIBARELI\b|\bLALGANJ\b/, 'Rae Bareli'],
  [/\bAMETHI\b|\bSHUKUL BAZAR\b|\bGAURIGANJ\b/, 'Amethi'],
  [/\bSULTANPUR\b|\bSULATANPUR\b/, 'Sultanpur'],
  [/\bBIJNAUR\b(?!.*\bLUCKNOW\b)/, 'Lucknow'],
  [/\bBIJNOR\b/, 'Bijnor'],
  [/\bBAHRAICH\b/, 'Bahraich'],
  [/\bBARELI\b(?!.*RAE)/, 'Bareilly'],
  [/\bLAKHIMPUR\b|\bGOLA\b|\bPALIA\b|\bKASTA\b/, 'Lakhimpur Kheri'],
  [/\bPRATAPGARH\b/, 'Pratapgarh'],
  [/\bAYODHYA\b|\bFAIZABAD\b/, 'Ayodhya'],
  [/\bGONDA\b/, 'Gonda'],
  [/\bFARRUKHABAD\b|\bFARUUKABAD\b/, 'Farrukhabad'],
  [/\bKANNAUJ\b|\bKANNOUJ\b/, 'Kannauj'],
  // The company writes Shahjahanpur as SHAJAPUR / SHAJANPUR / SAHJANPUR, and
  // Powayan is a town in that district. Read as Madhya Pradesh's Shajapur
  // until 5 Sep 2026 — the owner: "it is a district here in UP".
  [/\bSHAHJAHANPUR\b|\bSHAJAPUR\b|\bSHAJANPUR\b|\bSAHJANPUR\b|\bPOWAYAN\b|\bPOWYAN\b/, 'Shahjahanpur'],
  // The order desk's areas (26 Sep 2026): the districts the counters write, and how they misspell them.
  [/\bPRAYAGRAJ\b|\bPRYAGRAJ\b|\bPRAYAG RAJ\b|\bPRAYAGRAAJ\b|\bALLAHABAD\b/, 'Prayagraj'],
  [/\bBASTI\b/, 'Basti'],
  [/\bBALRAMPUR\b/, 'Balrampur'],
  [/\bFATEHPUR\b|\bFATEPUR\b/, 'Fatehpur'],
  [/\bAZAMGARH\b/, 'Azamgarh'],
];

/**
 * Since the map became all-India (27 Aug), the out-of-state areas resolve to
 * their real districts instead of falling off the edge — Delhi Auto's fleet
 * paints in Delhi. (SHAJAPUR is not one of them: see the UP list above.)
 */
const OUT_OF_STATE: Array<[RegExp, string]> = [
  [/\bNEW DELHI\b|\bDELHI\b/, 'Delhi'],
  [/\bMUMBAI\b/, 'Mumbai'],
  [/\bPATNA\b/, 'Patna'],
  [/\bHOSUR\b/, 'Krishnagiri'],
  [/\bSOLAPUR\b|\bSHOLAPUR\b/, 'Solapur'],
];

export function districtForArea(area: string): string | null {
  const up = area.toUpperCase();
  for (const [pattern, district] of OUT_OF_STATE) {
    if (pattern.test(up)) return district;
  }
  if (ROAD_IN_LUCKNOW.test(up)) return 'Lucknow';
  for (const [pattern, district] of DISTRICT_WORDS) {
    if (pattern.test(up)) return district;
  }
  return 'Lucknow';
}

/**
 * The district a piece of text actually NAMES, or null.
 *
 * `districtForArea` answers "Lucknow" for anything it does not recognise,
 * which is right for a book whose network is Lucknow-centred and wrong for
 * text read off a photo: OCR of a blurred Aadhaar would confidently place a
 * customer in Lucknow and, through the rent-agreement rule, decide whether a
 * sale may happen. Read text gets this instead — a district only when the
 * words say so (owner, 16 Sep 2026: "we need ocr as well").
 */
export function districtIfNamed(text: string | null | undefined): string | null {
  const up = (text ?? '').toUpperCase();
  if (!up.trim()) return null;
  for (const [pattern, district] of OUT_OF_STATE) {
    if (pattern.test(up)) return district;
  }
  if (ROAD_IN_LUCKNOW.test(up)) return 'Lucknow';
  for (const [pattern, district] of DISTRICT_WORDS) {
    if (pattern.test(up)) return district;
  }
  if (/\bLUCKNOW\b|\bLKO\b/.test(up)) return 'Lucknow';
  return null;
}

/** The GeoJSON's official names differ from the sheets' shorthand for two districts. */
const GEO_NAME: Record<string, string> = {
  KANPUR: 'Kanpur Nagar',
  RAEBARELI: 'Rae Bareli',
  'RAE BARELI': 'Rae Bareli',
  KHERI: 'Lakhimpur Kheri',
  LAKHIMPUR: 'Lakhimpur Kheri',
  FAIZABAD: 'Ayodhya',
  'NEW DELHI': 'Delhi',
};

export function geoDistrictName(recorded: string): string {
  const up = recorded.trim().toUpperCase();
  return (
    GEO_NAME[up] ??
    recorded
      .trim()
      .toLowerCase()
      .replace(/(^|\s|-)\p{L}/gu, (ch) => ch.toUpperCase())
  );
}

/**
 * A district as letters only, for comparing: the Aadhaar QR writes "Bara
 * Banki" where the RTO list writes "Barabanki" (owner, 10 Oct 2026: "Aadhar
 * is barabanki and rto also barabanki why is it asking for rent agreement").
 */
function districtKey(recorded: string): string {
  return geoDistrictName(recorded).toUpperCase().replace(/[^A-Z]/g, '');
}

/** Two recorded districts mean the same place? Compared on canonical names, spaces and punctuation aside. */
export function sameDistrict(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return true; // unknowable is never a violation
  return districtKey(a) === districtKey(b);
}
